/**
 * DesignerTabs — barra de tabs internos del Designer (B.6.d.2).
 *
 * Hoy solo "Designer" está activo. Los demás (User view, AI prompts,
 * JSON, Versions, Used in) están disabled con tooltip indicando el
 * sub-bloque destino. Cuando cada uno se implemente, se cambia
 * `enabled: true` aquí + se conecta su componente.
 */

import { useTranslation } from 'react-i18next'

export type DesignerTab =
  | 'designer'
  | 'user_view'
  | 'ai_prompts'
  | 'json'
  | 'versions'
  | 'used_in'

interface DesignerTabsProps {
  active: DesignerTab
  onChange: (tab: DesignerTab) => void
}

interface TabDef {
  key: DesignerTab
  enabled: boolean
  pendingKey?: keyof TabsPendingMap
}

interface TabsPendingMap {
  json: 'designer.tabs_pending.json'
  used_in: 'designer.tabs_pending.used_in'
}

const TABS: TabDef[] = [
  { key: 'designer', enabled: true },
  { key: 'user_view', enabled: true },
  { key: 'ai_prompts', enabled: true },
  { key: 'json', enabled: false, pendingKey: 'json' },
  { key: 'versions', enabled: true },
  { key: 'used_in', enabled: false, pendingKey: 'used_in' },
]

export function DesignerTabs({ active, onChange }: DesignerTabsProps) {
  const { t } = useTranslation('templates')
  return (
    <nav role="tablist" className="flex border-b border-line bg-surface">
      {TABS.map((tab) => {
        const label = t(`designer.tabs.${tab.key}` as 'designer.tabs.designer')
        if (!tab.enabled) {
          return (
            <span
              key={tab.key}
              role="tab"
              aria-disabled="true"
              title={
                tab.pendingKey
                  ? t(
                      `designer.tabs_pending.${tab.pendingKey}` as 'designer.tabs_pending.json',
                    )
                  : label
              }
              className="px-4 py-3 text-sm font-medium border-b-2 border-transparent text-ink-subtle cursor-not-allowed whitespace-nowrap"
            >
              {label}
            </span>
          )
        }
        const isActive = active === tab.key
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(tab.key)}
            className={
              isActive
                ? 'px-4 py-3 text-sm font-medium border-b-2 border-brand text-brand whitespace-nowrap'
                : 'px-4 py-3 text-sm font-medium border-b-2 border-transparent text-ink-muted hover:text-ink whitespace-nowrap'
            }
          >
            {label}
          </button>
        )
      })}
    </nav>
  )
}
